import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { isDesk } from "@/lib/api";
import { Brand, SHOW_SECTIONS } from "./Brand";

export const LangToggle = ({ testId = "lang-toggle" }) => {
  const { lang, setLang } = useSite();
  return (
    <div data-testid={testId} className="inline-flex border border-[#0B2A5B] text-xs font-bold">
      {["en", "ta"].map((l) => (
        <button
          key={l}
          data-testid={`${testId}-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 transition-colors ${lang === l ? "bg-[#0B2A5B] text-white" : "bg-white text-[#0B2A5B] hover:bg-blue-50"}`}
        >
          {l === "en" ? "EN" : "தமிழ்"}
        </button>
      ))}
    </div>
  );
};

export const SiteHeader = () => {
  const { categories, lang } = useSite();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const today = new Date().toLocaleDateString(lang === "ta" ? "ta-IN" : "en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const navCls = ({ isActive }) =>
    `whitespace-nowrap px-3 py-3 text-sm font-semibold transition-colors ${isActive ? "text-[#1D4ED8] border-b-2 border-[#1D4ED8]" : "text-zinc-700 hover:text-[#0B2A5B]"}`;

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b-2 border-[#0B2A5B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 py-3 sm:py-4">
          <Brand />
          <div className="flex flex-col items-end gap-1.5 shrink-0">
          <div className="flex items-center gap-3">
            <LangToggle />
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                {isDesk(user) && (
                  <button data-testid="header-desk-link" onClick={() => nav("/desk")} className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[#1D4ED8]">
                    <LayoutDashboard className="h-4 w-4" /> Desk
                  </button>
                )}
                <span data-testid="header-user-name" className="text-sm text-zinc-600">{user.name}</span>
                <button data-testid="header-logout-btn" onClick={logout} className="p-1.5 hover:bg-zinc-100" aria-label="Log out">
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Link data-testid="header-login-link" to="/login" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold hover:text-[#1D4ED8]">
                <User className="h-4 w-4" /> Sign in
              </Link>
            )}
            <button data-testid="mobile-menu-toggle" className="sm:hidden p-1.5" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
          <p data-testid="header-date" className="text-[10px] sm:text-[11px] uppercase tracking-[0.1em] text-zinc-500 text-right">{today}</p>
          </div>
        </div>
        <nav data-testid="category-nav" className="flex overflow-x-auto -mx-3 border-t border-zinc-200 [scrollbar-width:none]">
          <NavLink to="/" end className={navCls} data-testid="nav-home">{lang === "ta" ? "முகப்பு" : "Home"}</NavLink>
          {SHOW_SECTIONS && <NavLink to="/trend/breaking" className={navCls} data-testid="nav-breaking">{lang === "ta" ? "முக்கியம்" : "Breaking"}</NavLink>}
          {SHOW_SECTIONS && <NavLink to="/trend/hot" className={navCls} data-testid="nav-hot">{lang === "ta" ? "சூடான" : "Hot"}</NavLink>}
          {SHOW_SECTIONS && categories.map((c) => (
            <NavLink key={c.id} to={`/category/${c.slug}`} className={navCls} data-testid={`nav-cat-${c.slug}`}>
              {lang === "ta" ? c.name_ta : c.name_en}
            </NavLink>
          ))}
        </nav>
        {open && (
          <div data-testid="mobile-menu" className="sm:hidden border-t border-zinc-200 py-3 flex flex-col gap-2 text-sm font-semibold">
            {user ? (
              <>
                {isDesk(user) && <Link to="/desk" onClick={() => setOpen(false)} data-testid="mobile-desk-link">Newsroom desk</Link>}
                <button className="text-left" onClick={() => { logout(); setOpen(false); }} data-testid="mobile-logout-btn">Log out ({user.name})</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login-link">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} data-testid="mobile-register-link">Create reader account</Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
